import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Tv, Monitor, Smartphone, Layout, Settings, Cpu, Zap, Info, ShieldCheck } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia IPTV para Smart TV LG", href: "/blog/guia-iptv-lg" }
  ];

  const faq = [
    {
      question: "O sistema WebOS é compatível com IPTV?",
      answer: "Sim, todas as TVs LG com sistema WebOS (versão 3.0 ou superior) são totalmente compatíveis com os principais players de IPTV do mercado."
    },
    {
      question: "Quais os melhores apps para LG?",
      answer: "Os destaques na LG Content Store são o IBO Player, Bob Player e o XCIPTV. Eles oferecem a melhor estabilidade para o hardware da LG."
    },
    {
      question: "Preciso baixar o app por um pendrive?",
      answer: "Não. Na LG, você baixa os aplicativos diretamente da loja oficial da TV, o que garante segurança e atualizações automáticas."
    }
  ];

  const benefits = [
    {
      title: "Controle Magic Remote",
      description: "Navegue pelo IPTV usando o cursor do mouse na tela, facilitando a escolha de filmes e canais.",
      icon: <Tv className="w-6 h-6" />
    },
    {
      title: "Painel OLED Otimizado",
      description: "Nossas transmissões em 4K HDR aproveitam cada pixel dos painéis OLED da LG.",
      icon: <Layout className="w-6 h-6" />
    },
    {
      title: "Estabilidade WebOS",
      description: "Um dos sistemas mais fluidos do mundo para rodar aplicativos de streaming pesados.",
      icon: <Cpu className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "LG Content Store",
      description: "Acesse a loja oficial da sua TV pelo menu principal."
    },
    {
      title: "Instale o Player",
      description: "Busque por 'IBO Player' e realize a instalação em segundos."
    },
    {
      title: "Configuração P2 PRO",
      description: "Insira seu usuário e senha no aplicativo escolhido."
    },
    {
      title: "Pronto para Assistir",
      description: "Curta o melhor do cinema e dos esportes com imagem de elite."
    }
  ];

  const diferenciais = [
    {
      title: "Configurações de Brilho",
      description: "Dicas para ajustar sua LG para melhor visualização de conteúdos escuros em 4K.",
      icon: <Settings className="w-6 h-6" />
    },
    {
      title: "Segurança de Dados",
      description: "Protocolos de autenticação que protegem sua conta dentro do WebOS.",
      icon: <ShieldCheck className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. IPTV na LG: A Revolução do Entretenimento em WebOS",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            A LG consolidou-se como uma das gigantes do entretenimento doméstico com seu premiado sistema <strong>WebOS</strong>. Para os usuários de IPTV, a LG oferece uma das interfaces mais amigáveis e rápidas do mercado. Quando você conecta o sinal de alta performance da <strong>P2 PRO</strong> em uma Smart TV LG, você não está apenas assistindo TV; você está elevando o nível do seu lazer para um padrão profissional.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia exaustivo, vamos desvendar todos os segredos para configurar o IPTV na sua LG. Desde o uso do controle 'Magic Remote' para uma navegação precisa até os ajustes de rede que garantem que o bitrate da nossa imagem 4K nunca caia. Se você busca fidelidade de cores e pretos profundos, a combinação LG + P2 PRO é a escolha definitiva em 2026.
          </p>
        </div>
      )
    },
    {
      title: "2. O Poder do Magic Remote no IPTV",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Zap className="w-6 h-6 text-electric" /> Navegação 'Point-and-Click'</h4>
              <p className="text-sm text-muted-foreground">Diferente de outras TVs, na LG você usa o controle como um mouse. Isso torna a busca por filmes no nosso VOD 3x mais rápida.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Settings className="w-6 h-6 text-amber-500" /> Atalhos Customizados</h4>
              <p className="text-sm text-muted-foreground">Ensinamos como mapear botões do seu controle LG para abrir seu aplicativo de IPTV favorito instantaneamente.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Ajustes Técnicos para Performance OLED/Nanocell",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Tire o máximo proveito da tela da sua LG com estes ajustes finos:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Desative o Modo 'Eco'</h4>
              <p className="text-muted-foreground">O modo de economia de energia da LG pode limitar o processamento do Wi-Fi e do app de IPTV. Mantenha-o desligado para evitar 'lag'.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Configuração de IPv6</h4>
              <p className="text-sm text-muted-foreground">Em algumas redes, desativar o IPv6 nas configurações de rede da TV LG resolve problemas de conexão com servidores de streaming.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Checklist de Instalação Profissional",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Siga estes passos para garantir que sua LG esteja pronta para o sinal de elite:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Região da Conta LG</h5>
                <p className="text-sm text-muted-foreground">Certifique-se de que sua conta LG está configurada como 'Brasil' para ter acesso a todos os aplicativos otimizados na loja.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">DNS Cloudflare (1.1.1.1)</h5>
                <p className="text-sm text-muted-foreground">Na LG, o DNS da Cloudflare costuma ter uma latência menor que o do Google, ideal para zapping rápido.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Dica de OLED</h5>
                <p className="text-sm text-muted-foreground">Ao assistir canais com logotipos estáticos, use as funções de proteção de tela da LG para garantir a longevidade do seu painel.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias Dispositivos"
        title="Guia IPTV para Smart TV LG: Configuração WebOS 2026"
        subtitle="O passo a passo definitivo para configurar o IPTV de elite na sua LG. Performance, praticidade e imagem impecável."
        heroImage="https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "As Smart TVs LG são verdadeiras máquinas de entretenimento, especialmente as linhas OLED e Nanocell. No entanto, para desfrutar de milhares de canais com a qualidade que sua TV merece, é preciso uma configuração precisa. Na P2 PRO, otimizamos nossas listas para o sistema WebOS, garantindo que você tenha a melhor experiência de streaming do mercado.",
          "Neste guia técnico, vamos detalhar desde a busca na LG Content Store até as configurações avançadas de imagem que fazem a diferença em transmissões 4K HDR. Se você possui uma LG, você tem em mãos um dos melhores dispositivos para IPTV do mundo, e nós vamos te ajudar a extrair cada gota de performance dela.",
          "Prepare-se para transformar a sua sala em um estádio de futebol ou em uma sala de cinema premium. Com a LG e a P2 PRO, o limite é a sua imaginação."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="💎 Sua LG Merece o Melhor Sinal"
        ctaDescription="A P2 PRO é a escolha favorita dos donos de Smart TVs LG no Brasil. Peça seu teste e veja por quê."
        ctaButtonText="Testar Agora na Minha LG"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-iptv-lg")({
  head: () => ({
    meta: [
      { title: "Guia IPTV para Smart TV LG: Passo a Passo WebOS 2026" },
      { name: "description", content: "Aprenda a instalar IPTV na Smart TV LG. Melhores aplicativos, configuração de DNS, uso do Magic Remote e performance máxima no sistema WebOS." },
    ],
  }),
  component: ArticlePage,
});
